import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { fetchOrders } from '@/store/slices/ordersSlice';
import { Order } from '@/types';
import { ROUTES } from '@/utils/constants';
import Button from '@/components/ui/Button';
import { getPrimaryImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import {
  EyeIcon,
  TruckIcon,
  CreditCardIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ShoppingBagIcon,
  FunnelIcon,
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const OrdersPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, isLoading, error } = useSelector((state: RootState) => state.orders);
  
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    try {
      dispatch(fetchOrders());
    } catch (err) {
      console.error('Error dispatching fetchOrders:', err);
      setHasError(true);
    }
  }, [dispatch]);

  // Load filters from localStorage
  useEffect(() => {
    const savedStatus = localStorage.getItem('orders_status_filter');
    const savedPayment = localStorage.getItem('orders_payment_filter');
    if (savedStatus) setStatusFilter(savedStatus);
    if (savedPayment) setPaymentFilter(savedPayment);
  }, []);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('orders_status_filter', statusFilter);
    localStorage.setItem('orders_payment_filter', paymentFilter);
  }, [statusFilter, paymentFilter]);

  // Filter orders
  const filteredOrders = useMemo(() => {
    try {
      if (!orders || !Array.isArray(orders)) return [];
      return orders.filter((order: Order) => {
        if (!order) return false;
        const statusMatch = statusFilter === 'all' || order.order_status === statusFilter;
        const paymentMatch = paymentFilter === 'all' || (order.payment_status && order.payment_status === paymentFilter);
        return statusMatch && paymentMatch;
      });
    } catch (err) {
      console.error('Error filtering orders:', err);
      return [];
    }
  }, [orders, statusFilter, paymentFilter]);

  const clearFilters = () => {
    setStatusFilter('all');
    setPaymentFilter('all');
  };

  const hasActiveFilters = statusFilter !== 'all' || paymentFilter !== 'all';

  const getStatusIcon = (status: string | undefined) => {
    if (!status) return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'bg-yellow-100 text-yellow-800';
    switch (status) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleViewOrder = (orderId: number) => {
    navigate(`${ROUTES.ORDERS}/${orderId}`);
  };

  // Debug: Log component state (only on mount and when key values change)
  useEffect(() => {
    console.log('OrdersPage mounted');
  }, []); // Only run once on mount
  
  useEffect(() => {
    console.log('OrdersPage state changed:', { isLoading, error, ordersCount: orders?.length, hasError });
  }, [isLoading, error, hasError]); // Removed 'orders' to prevent infinite loops

  // Always show something - even during initial load
  if (isLoading === undefined) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  if ((error || hasError) && (!orders || orders.length === 0)) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : error?.toString() || 'An error occurred';
    
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 mb-4">
            <XCircleIcon className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Orders</h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => {
              setHasError(false);
              dispatch(fetchOrders());
            }}>
              Retry
            </Button>
            <Button variant="outline" onClick={() => navigate(ROUTES.PRODUCTS)}>
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Ensure we always have a safe orders array
  const safeOrders = Array.isArray(orders) ? orders : [];
  const safeFilteredOrders = Array.isArray(filteredOrders) ? filteredOrders : [];

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Debug banner - remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-2 p-1 bg-blue-50 text-blue-700 text-xs rounded">
            Debug: {safeOrders.length} orders loaded
          </div>
        )}
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
              <p className="mt-2 text-gray-600">
                Track and manage your orders ({safeFilteredOrders.length} {safeFilteredOrders.length === 1 ? 'order' : 'orders'})
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2"
            >
              <FunnelIcon className="h-4 w-4" />
              <span>Filters</span>
            </Button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Payment Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Status
                  </label>
                  <select
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="all">All Payment Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="mt-4 flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <XMarkIcon className="h-4 w-4 mr-1" />
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {typeof error === 'string' ? error : error?.toString() || 'An error occurred'}
          </div>
        )}

        {safeFilteredOrders.length === 0 && safeOrders.length > 0 ? (
          /* No Results with Filters */
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <FunnelIcon className="h-24 w-24 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders match your filters</h3>
            <p className="text-gray-600 mb-6">Try adjusting your filter criteria.</p>
            <Button onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        ) : safeFilteredOrders.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="text-gray-400 mb-6">
              <ShoppingBagIcon className="h-32 w-32 mx-auto" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">No orders yet</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              You haven't placed any orders yet. Start shopping to see your order history here!
            </p>
            <Button 
              onClick={() => navigate(ROUTES.PRODUCTS)}
              className="px-8 py-3"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          /* Orders List */
          <div className="space-y-6">
            {safeFilteredOrders.map((order: Order) => {
              if (!order) return null;
              return (
              <div key={order.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Order Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(order.order_status)}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.order_status)}`}>
                          {order.order_status ? order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1) : 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Order #{order.order_number}
                        </h3>
                        <p className="text-sm text-gray-600">
                          Placed on {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-lg font-semibold text-gray-900">
                          {formatCurrency(Number(order.total_amount || 0))}
                        </p>
                        <p className="text-sm text-gray-600">
                          {order.items?.length || 0} item{(order.items?.length || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={() => handleViewOrder(order.id)}
                      >
                        <EyeIcon className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Order Items Preview */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Items */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Items</h4>
                      <div className="space-y-2">
                        {order.items?.slice(0, 3).map((item) => (
                          <div key={item.id} className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <img
                                src={getPrimaryImage(item.product?.images)}
                                alt={item.product?.name || 'Product'}
                                className="w-12 h-12 object-cover rounded-md"
                                loading="lazy"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {item.product?.name}
                              </p>
                              <p className="text-sm text-gray-600">Qty: {item.quantity}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-gray-900">
                                {formatCurrency(Number(item.total_price || 0))}
                              </p>
                            </div>
                          </div>
                        ))}
                        {order.items && order.items.length > 3 && (
                          <p className="text-sm text-gray-600">
                            +{order.items.length - 3} more item{(order.items.length - 3) !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Order Info */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Order Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <TruckIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 capitalize">
                            {order.shipping_method ? order.shipping_method.replace('_', ' ') : 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <CreditCardIcon className="h-4 w-4 text-gray-400" />
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
                          </span>
                        </div>
                        {order.advance_amount && (
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-600">Advance:</span>
                            <span className="font-medium">{formatCurrency(Number(order.advance_amount))}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      Last updated: {new Date(order.updated_at).toLocaleDateString()}
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => handleViewOrder(order.id)}
                    >
                      View Full Details
                    </Button>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Wrap component in error boundary-like safety
const OrdersPageWithErrorHandling: React.FC = () => {
  try {
    return <OrdersPage />;
  } catch (error) {
    console.error('OrdersPage error:', error);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-500 mb-4">
            <ExclamationTriangleIcon className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">There was an error loading the orders page. Please try refreshing.</p>
          <Button onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }
};

export default OrdersPageWithErrorHandling;
